import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Stars, GOOGLE_REVIEWS_URL } from "./Reviews";

const EMPTY = { name: "", rating: 5, text: "", service: "", date: "" };
const inputCls = "bg-[#141414] border-white/10 text-white rounded-sm focus-visible:ring-0 focus-visible:border-[#FF2079]";

function ReviewDialog({ open, review, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { if (open) setForm(review ? { ...EMPTY, ...review } : EMPTY); }, [open, review]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    const { id, created_at, ...body } = form;
    onSave({ ...body, rating: Number(body.rating) });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="review-dialog" className="bg-[#0E0E0E] border border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-white font-light">{review ? "Edit review" : "Add review"}</DialogTitle>
          <DialogDescription className="text-[#A09CA3]">Copy a real review from Google or one a client sent you.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <Input data-testid="review-name-input" required minLength={2} placeholder="Client name (e.g. Harpreet S.)" value={form.name} onChange={set("name")} className={inputCls} />
          <select data-testid="review-rating-input" value={form.rating} onChange={set("rating")} className={`${inputCls} w-full h-10 px-3 border`}>
            {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} star{r > 1 ? "s" : ""}</option>)}
          </select>
          <Textarea data-testid="review-text-input" required minLength={5} placeholder="What they said" value={form.text} onChange={set("text")} className={`${inputCls} min-h-[110px]`} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="review-service-input" placeholder="Service (optional)" value={form.service} onChange={set("service")} className={inputCls} />
            <Input data-testid="review-date-input" placeholder="When (e.g. May 2026)" value={form.date} onChange={set("date")} className={inputCls} />
          </div>
          <Button data-testid="review-save-btn" type="submit" className="w-full pink-glow-btn bg-[#FF2079] hover:bg-[#D91666] text-white rounded-sm h-11 text-[11px] tracking-[0.28em] uppercase">Save review</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [dialog, setDialog] = useState({ open: false, review: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/reviews");
    setReviews(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    try {
      if (dialog.review) await api.put(`/reviews/${dialog.review.id}`, body);
      else await api.post("/reviews", body);
      toast.success("Review saved.");
      setDialog({ open: false, review: null });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove review from ${r.name}?`)) return;
    await api.delete(`/reviews/${r.id}`);
    toast.success("Review removed.");
    load();
  };

  return (
    <div data-testid="reviews-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="eyebrow">Client Reviews</div>
        <div className="flex gap-3">
          <Button asChild variant="outline" className="bg-transparent border-white/10 text-white hover:bg-white/5 rounded-sm h-10 px-5 text-[10px] tracking-[0.28em] uppercase">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer" data-testid="open-google-reviews-btn">Open Google</a>
          </Button>
          <Button data-testid="add-review-btn" onClick={() => setDialog({ open: true, review: null })} className="pink-glow-btn bg-[#FF2079] hover:bg-[#D91666] text-white rounded-sm h-10 px-5 text-[10px] tracking-[0.28em] uppercase">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Review
          </Button>
        </div>
      </div>
      <div className="glass-card rounded-sm">
        {reviews.length === 0 && <div className="p-8 text-center text-[#A09CA3]">No reviews yet. Add your favourite real client reviews.</div>}
        {reviews.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className="flex items-start justify-between gap-4 px-5 py-4 border-b border-white/5 last:border-0">
            <div className="min-w-0">
              <div className="flex items-center gap-3"><span className="text-white">{r.name}</span><Stars rating={r.rating} size="w-3 h-3" /></div>
              <p className="text-sm text-[#A09CA3] line-clamp-2 mt-1">{r.text}</p>
            </div>
            <div className="flex shrink-0">
              <button data-testid={`review-edit-${r.id}-btn`} onClick={() => setDialog({ open: true, review: r })} className="p-2 text-[#A09CA3] hover:text-white" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`review-delete-${r.id}-btn`} onClick={() => remove(r)} className="p-2 text-[#A09CA3] hover:text-red-400" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <ReviewDialog open={dialog.open} review={dialog.review} onSave={save} onClose={() => setDialog({ open: false, review: null })} />
    </div>
  );
}
